import { motion } from "framer-motion";
import { useEffect } from "react";
import { useScene } from "./scene/SceneController";

const futureFeatures = [
  {
    title: "AI Workflow Intelligence",
    description: "Systems understand processes, identify bottlenecks and help teams move faster.",
  },
  {
    title: "Firm-Wide Visibility",
    description: "Partners get a complete view of operations without chasing updates.",
  },
  {
    title: "Predictive Insights",
    description: "Move from reacting to problems toward making proactive decisions.",
  },
  {
    title: "Exceptional Client Experience",
    description: "Deliver faster responses and smoother communication at scale.",
  },
];

const VisionSection = () => {
    const brandName = process.env.REACT_APP_BRAND_NAME;
  const { updateScene } = useScene();

  useEffect(() => {
    updateScene({
      section: "vision",
      chaos: 0,
      organization: 1,
      coreIntensity: 4,
      cameraDistance: 6,
    });
  }, [updateScene]);

  return (
    <section
      id="vision"
      className="tw-relative tw-min-h-screen tw-py-32 tw-px-6 tw-overflow-hidden"
    >
      <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-white tw-via-blue-50 tw-to-gray-100" />

      <motion.div
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
        }}
        className="tw-absolute tw-top-40 tw-left-1/2 tw--translate-x-1/2 tw-w-[500px] tw-h-[500px] tw-rounded-full tw-bg-blue-400/30 tw-blur-[120px]"
      />

      <div className="tw-relative tw-z-10 tw-max-w-6xl tw-mx-auto">
        <motion.div
          initial={{
            opacity: 0,
            y: 40,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.8,
          }}
          className="tw-text-center tw-max-w-4xl tw-mx-auto"
        >
          <p className="tw-text-blue-600 tw-uppercase tw-tracking-[0.3em] tw-text-sm tw-mb-5">
            The future of accounting
          </p>

          <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-text-gray-900">
            The CA firm of tomorrow
            <br />
            runs on intelligence.
          </h2>

          <p className="tw-mt-6 tw-text-lg tw-text-gray-600 tw-leading-relaxed">
            {brandName} connects people, processes and information into one intelligent operating system built for the next generation of professional firms.
          </p>
        </motion.div>

        <motion.div
          initial={{
            opacity: 0,
            scale: 0.85,
          }}
          whileInView={{
            opacity: 1,
            scale: 1,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 1,
          }}
          className="tw-mt-20 tw-mx-auto tw-max-w-4xl tw-rounded-[40px] tw-p-10 tw-bg-white/70 tw-backdrop-blur-2xl tw-border tw-border-white tw-shadow-2xl"
        >
          <div className="tw-relative tw-flex tw-justify-center tw-items-center tw-h-64">
            <motion.div
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "linear",
              }}
              className="tw-absolute tw-w-52 tw-h-52 tw-rounded-full tw-border tw-border-blue-300"
            />

            <motion.div
              animate={{
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
              }}
              className="tw-w-28 tw-h-28 tw-rounded-full tw-bg-gradient-to-r tw-from-blue-500 tw-to-cyan-400 tw-shadow-[0_0_80px_rgba(34,211,238,0.8)]"
            />
          </div>

          <div className="tw-text-center">
            <h3 className="tw-text-3xl tw-font-bold tw-text-gray-900">
              {brandName} Intelligence Engine
            </h3>

            <p className="tw-mt-3 tw-text-gray-600">
              One connected system powering smarter workflows across your firm.
            </p>
          </div>
        </motion.div>

        <div className="tw-mt-20 tw-grid md:tw-grid-cols-2 tw-gap-8">
          {futureFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{
                opacity: 0,
                y: 40,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                delay: index * 0.15,
              }}
              className="tw-rounded-3xl tw-p-8 tw-bg-white tw-shadow-lg tw-border tw-border-gray-100"
            >
              <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-3">
                {feature.title}
              </h3>

              <p className="tw-text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VisionSection;